import { useCallback, useMemo } from "react";
import { contentLocaleOf, useI18n } from "../../lib/i18n";
import { pickText } from "../../lib/locales";
import type { SearchOption } from "../ui/SearchSelect";
import { Thumb } from "../ui/Thumb";

/**
 * 合成物品在管端的统一画法：图标 +「名称(id)」，名称按当前界面语言取。
 *
 * 之前各页各写各的：配置页的下拉是「id · 英文名」，订单 / 玩家面板 / 删除页只有 id，
 * 名字一律取英文（pickText 没传语言）。运营认物品靠图和名字，名字放前面（用户 2026-11-02 定）；
 * id 留在括号里不删：发布校验和服务端报错里说的都是 id。
 *
 * 只要图标和多语言名字两样，配置页的 MergeItem、删除页的选项、玩家面板取来的那一版配置都满足。
 * 查不到的 id（配置里删掉了、玩家面板那一版配置还没取到）退成只写 id。
 */
export type MergeItemLook = { icon: string; nameI18n: Record<string, string> };
export type MergeItemIndex = ReadonlyMap<string, MergeItemLook>;

export function mergeItemIndex(items: readonly ({ itemId: string } & MergeItemLook)[] | null | undefined): MergeItemIndex {
  return new Map((items ?? []).map((it) => [it.itemId, it]));
}

export function useMergeItemIndex(items: readonly ({ itemId: string } & MergeItemLook)[] | null | undefined): MergeItemIndex {
  return useMemo(() => mergeItemIndex(items), [items]);
}

/**
 * 配置里的名字按界面语言取（中文界面取简中），没填的回退英文；全没填返回空串，由调用方决定写不写 id。
 * 合成页的 NPC 名字也走这里，和物品一个口径
 */
export function useLocalizedName(): (v: { nameI18n: Record<string, string> } | undefined) => string {
  const { lang } = useI18n();
  const locale = contentLocaleOf(lang);
  return useCallback((v) => (v ? pickText(v.nameI18n, locale) : ""), [locale]);
}

/** 「名称(id)」；没有名字就只写 id。下拉选项、悬停提示这类只能放纯文字的地方用 */
export const nameWithId = (name: string, id: string) => (name ? `${name}(${id})` : id);

/** 选物品的下拉选项：「名称(id)」配图标，搜索 id 和名字都能命中 */
export function useMergeItemOptions(items: readonly ({ itemId: string } & MergeItemLook)[]): SearchOption[] {
  const nameOf = useLocalizedName();
  return useMemo(() => items.map((it) => {
    const name = nameOf(it);
    return { value: it.itemId, label: nameWithId(name, it.itemId), icon: it.icon || undefined };
  }), [items, nameOf]);
}

export function MergeItemRef({ id, look, qty, compact, className = "" }: {
  id: string;
  look: MergeItemLook | undefined;
  /** 给了就在后面写「×n」（订单需求、奖励这类） */
  qty?: number;
  /** 挤的地方只写名字，「名称(id)」放进悬停提示；没有名字时照写 id */
  compact?: boolean;
  className?: string;
}) {
  const name = useLocalizedName()(look);
  return (
    <span className={`inline-flex min-w-0 items-center gap-1 align-middle ${className}`} title={compact && name ? nameWithId(name, id) : undefined}>
      {look?.icon ? <Thumb src={look.icon} title={nameWithId(name, id)} className="h-5 w-5 flex-none object-contain" /> : null}
      {name ? (
        <span className="truncate">
          {name}{compact ? null : <span className="text-ink-placeholder">(<span className="font-mono">{id}</span>)</span>}
        </span>
      ) : <span className="font-mono">{id}</span>}
      {qty != null ? <span className="flex-none tabular-nums">×{qty}</span> : null}
    </span>
  );
}

/** 一串物品（订单需求、奖励、删除清单）：逐个画，放不下就换行 */
export function MergeItemList({ lines, index, empty = "—" }: {
  lines: ReadonlyArray<{ id: string; qty?: number }>;
  index: MergeItemIndex;
  empty?: string;
}) {
  if (!lines.length) return <>{empty}</>;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
      {lines.map((x, i) => <MergeItemRef key={`${x.id}:${i}`} id={x.id} look={index.get(x.id)} qty={x.qty} />)}
    </span>
  );
}
